import React, { useEffect, useRef, useState } from "react";
import { Image as ImageIcon, ChevronDown, ChevronUp } from "lucide-react";
import { isIOS, isAndroid } from "@/lib/push";

const esMovil = () => isIOS() || isAndroid();
const CANCELACION_INSTANTANEA = 1500;

// Cámara con alternativa: si el móvil no abre la cámara directamente, se sugiere la galería sin tratarlo como error.
export const useCamara = (onFile) => {
  const camRef = useRef(null);
  const galRef = useRef(null);
  const recibido = useRef(false);
  const limpiar = useRef(() => {});
  const [estado, setEstado] = useState(null); // null | "fallo"

  useEffect(() => () => limpiar.current(), []);

  // Sin temporizadores: la cámara se considera fallida solo si el navegador cancela el selector
  // casi al instante y sin que la página haya llegado a ocultarse (la cámara nunca se abrió).
  const abrir = () => {
    limpiar.current();
    setEstado(null);
    recibido.current = false;
    let salio = false;
    const inicio = Date.now();
    const alSalir = () => { salio = true; };
    const onVis = () => { if (document.visibilityState === "hidden") alSalir(); };
    const alCancelar = () => {
      limpiar.current();
      if (!salio && !recibido.current && Date.now() - inicio < CANCELACION_INSTANTANEA) setEstado("fallo");
    };
    const input = camRef.current;
    window.addEventListener("blur", alSalir);
    document.addEventListener("visibilitychange", onVis);
    input?.addEventListener("cancel", alCancelar);
    limpiar.current = () => {
      window.removeEventListener("blur", alSalir);
      document.removeEventListener("visibilitychange", onVis);
      input?.removeEventListener("cancel", alCancelar);
    };
    try { input?.click(); } catch { setEstado("fallo"); }
  };

  const onChange = (e) => {
    const f = e.target.files && e.target.files[0];
    if (f) { recibido.current = true; limpiar.current(); setEstado(null); }
    onFile(e);
  };

  const inputs = (
    <>
      <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onChange} data-testid="camara-input" />
      <input ref={galRef} type="file" accept="image/*" className="hidden" onChange={onChange} data-testid="galeria-input" />
    </>
  );
  const elegirGaleria = () => { setEstado(null); galRef.current?.click(); };
  const aviso = estado ? <AvisoCamara onGaleria={elegirGaleria} /> : null;
  return { abrir, inputs, aviso, elegirGaleria, esMovil: esMovil() };
};

const navegador = () => {
  const ua = navigator.userAgent || "";
  if (/FBAN|FBAV|Instagram|WhatsApp|Line\//.test(ua)) return "app";
  if (isIOS()) return /CriOS/.test(ua) ? "ios-chrome" : /FxiOS|EdgiOS/.test(ua) ? "ios-otro" : "ios-safari";
  if (isAndroid()) return /SamsungBrowser|EdgA|OPR|Firefox/.test(ua) ? "android-otro" : "android-chrome";
  return "otro";
};

const PASOS = {
  "android-chrome": ["Abre los Ajustes del teléfono.", "Entra en Aplicaciones → Chrome.", "Elige Permisos → Cámara y comprueba que esté permitida.", "En Chrome, toca el icono que hay junto a aldiafacil.es en la barra de direcciones → Permisos, y comprueba que la Cámara no esté bloqueada.", "Vuelve a AL DÍA e inténtalo de nuevo."],
  "android-otro": ["Abre los Ajustes del teléfono.", "Entra en Aplicaciones y busca el navegador que estás usando.", "Elige Permisos → Cámara y comprueba que esté permitida.", "Vuelve a AL DÍA e inténtalo de nuevo. Si sigue igual, prueba a abrir aldiafacil.es en Chrome."],
  "ios-safari": ["Abre los Ajustes del iPhone.", "Entra en Safari (en algunas versiones, Apps → Safari).", "En «Cámara», elige Preguntar o Permitir.", "Vuelve a AL DÍA e inténtalo de nuevo."],
  "ios-chrome": ["Abre los Ajustes del iPhone.", "Busca la app Chrome en la lista.", "Comprueba que la Cámara esté activada.", "Vuelve a AL DÍA e inténtalo de nuevo."],
  "ios-otro": ["Abre los Ajustes del iPhone.", "Busca en la lista el navegador que estás usando.", "Comprueba que la Cámara esté activada.", "Vuelve a AL DÍA e inténtalo de nuevo."],
  app: ["Parece que has abierto AL DÍA dentro de otra aplicación, que puede no permitir la cámara.", "Abre aldiafacil.es directamente en Chrome (Android) o Safari (iPhone).", "Inténtalo de nuevo desde allí."],
  otro: ["Revisa en los ajustes del navegador que la cámara esté permitida para aldiafacil.es.", "Vuelve a AL DÍA e inténtalo de nuevo."],
};

const AvisoCamara = ({ onGaleria }) => {
  const [ayuda, setAyuda] = useState(false);
  const pasos = PASOS[navegador()];
  return (
    <div className="mt-4 bg-[#F6F3EE] rounded-2xl p-4 text-left" role="status" data-testid="camara-aviso-fallo">
      <p className="text-base font-semibold text-[#1C2421]">No hemos podido abrir la cámara</p>
      <p className="text-base text-[#4A5550] mt-1">Puedes hacer la foto con la app de cámara y elegirla desde la galería.</p>
      <button type="button" onClick={onGaleria} data-testid="camara-seleccionar-foto"
        className="mt-3 min-h-[48px] px-4 rounded-xl bg-white border-2 border-[#E5DFD5] text-[#1C2421] font-semibold text-base inline-flex items-center gap-2">
        <ImageIcon size={20} /> Elegir de la galería
      </button>
      <button type="button" onClick={() => setAyuda((v) => !v)} aria-expanded={ayuda} data-testid="camara-como-activar"
        className="mt-2 min-h-[44px] w-full text-left text-base font-semibold text-[#3A6B56] inline-flex items-center gap-1">
        ¿Cómo activar la cámara? {ayuda ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
      </button>
      {ayuda && (
        <ol className="mt-1 space-y-2 list-decimal pl-6 text-base text-[#1C2421]" data-testid={`camara-pasos-${navegador()}`}>
          {pasos.map((t) => <li key={t}>{t}</li>)}
        </ol>
      )}
    </div>
  );
};
